import type { AuthorPage } from '$models/Page/AuthorPage';

export type AuthorLike = {
	name: string;
	jobTitle: string;
	companyName: string;
	summary: string;
	image: { url: string } | null;
	knowledgeAreas: { name: string }[];
	linkedinUrl: string;
};

/**
 * Person schema.org object for a blog author, shared by the author page and blog articles.
 */
export function buildPersonSchema(author: AuthorLike, url: string): Record<string, unknown> {
	return {
		'@type': 'Person',
		'@id': `${url}#person`,
		name: author.name,
		url,
		jobTitle: author.jobTitle || undefined,
		worksFor: author.companyName
			? { '@type': 'Organization', name: author.companyName }
			: undefined,
		description: author.summary || undefined,
		image: author.image?.url,
		knowsAbout: author.knowledgeAreas.length
			? author.knowledgeAreas.map((area) => area.name)
			: undefined,
		sameAs: author.linkedinUrl ? [author.linkedinUrl] : undefined
	};
}

/**
 * ProfilePage schema.org object for an author page.
 * @see https://developers.google.com/search/docs/appearance/structured-data/profile-page
 */
export function buildProfilePageSchema(page: AuthorPage, url: URL): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'ProfilePage',
		mainEntity: buildPersonSchema({ ...page, name: page.title }, `${url.origin}${url.pathname}`)
	};
}
